import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, Star, ArrowRight, MessageCircle, ShieldCheck, Users, Sparkles, Plus } from 'lucide-react';
import { api, getPublicImageUrl } from '../../utils/api';
import { getBookingAction } from '../../utils/auth';
import { toast } from 'sonner';
import Header from '../shared/Header';
import Footer from '../shared/Footer';
import SEO from '../shared/SEO';

const HomePage = () => {
  const [hairstyles, setHairstyles] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [brandSettings, setBrandSettings] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.getPublicHairstyles({ featured: true }).then(res => setHairstyles(res.data.slice(0, 6))).catch(console.error);
    api.getTestimonials({ featured: true }).then(res => setTestimonials(res.data.slice(0, 3))).catch(console.error);
    api.getBrandSettings().then(res => setBrandSettings(res.data)).catch(console.error);
  }, []);

  const whatsappClick = () => {
    if (brandSettings?.whatsapp_link) {
      window.open(brandSettings.whatsapp_link, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFBF9]">
      <SEO
        description="Premium hair styling services in Nigeria. Expert braiding, weaving, cornrows, wig installation, and natural hair care. Book your crown today."
        path="/"
      />
      <Header />

      {/* ===== HERO SECTION ===== */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_preshy-launch/artifacts/lewjs41v_1000218318.png"
            alt="Professional hair braiding at Crowns by Preshy"
            className="w-full h-full object-cover object-[65%_center]"
            loading="eager"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2A1610]/95 via-[#2A1610]/70 to-[#2A1610]/30 sm:from-[#2A1610]/90 sm:via-[#2A1610]/50 sm:to-transparent"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <div className="min-h-[520px] sm:min-h-[560px] lg:min-h-[620px] flex items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="py-16 sm:py-20 lg:py-24 max-w-xl lg:max-w-lg"
            >
              <Crown className="h-8 w-8 sm:h-10 sm:w-10 text-[#C9A961] mb-4" strokeWidth={1.5} />
              <p className="text-[#C9A961] text-xs sm:text-sm font-medium tracking-[0.2em] uppercase mb-4">
                Premium Hair. Timeless You.
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-2 tracking-tight font-light leading-[1.1]">
                Wear Your
              </h1>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#C9A961] mb-2 tracking-tight font-light leading-[1.1]">
                Crown
              </h1>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-4 tracking-tight font-light leading-[1.1]">
                Beautifully
              </h1>

              {/* Diamond Separator */}
              <div className="flex items-center gap-3 mb-5">
                <div className="h-[1px] w-16 sm:w-24 bg-[#C9A961]"></div>
                <div className="h-2 w-2 bg-[#C9A961] rotate-45"></div>
                <div className="h-[1px] w-16 sm:w-24 bg-[#C9A961]"></div>
              </div>

              <p className="text-sm sm:text-base text-white/80 mb-8 leading-relaxed max-w-md">
                Luxury braids, weaves, and elegant hairstyling — crafted with precision, passion, and premium care.
              </p>

              <div className="flex flex-wrap gap-3 sm:gap-4">
                <Link
                  to="/gallery"
                  className="bg-[#C9A961] text-[#3E2723] hover:bg-[#d4b876] rounded-full px-6 sm:px-8 py-3 inline-flex items-center gap-2 font-medium text-sm sm:text-base transition-all hover:-translate-y-0.5"
                  data-testid="hero-gallery-button"
                >
                  View Gallery
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/services"
                  className="bg-white/10 backdrop-blur-sm border border-white/30 text-white hover:bg-white/20 rounded-full px-6 sm:px-8 py-3 inline-flex items-center gap-2 text-sm sm:text-base transition-all"
                  data-testid="hero-services-button"
                >
                  Our Services
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== TRUST BADGES ===== */}
      <section className="bg-white border-b border-[#E6DCD3]" data-testid="trust-badges-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <div className="grid grid-cols-3 divide-x divide-[#E6DCD3]">
            {[
              { icon: ShieldCheck, title: 'Premium Quality', desc: 'Top-grade hair only' },
              { icon: Users, title: 'Expert Stylists', desc: 'Skilled. Professional. Caring.' },
              { icon: Sparkles, title: 'Luxury Experience', desc: 'Relax & feel like royalty' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 sm:gap-3 py-4 sm:py-5 px-1 sm:px-4 justify-center">
                <item.icon className="h-5 w-5 sm:h-6 sm:w-6 text-[#C9A961] flex-shrink-0 hidden sm:block" strokeWidth={1.5} />
                <div className="min-w-0 text-center sm:text-left">
                  <p className="text-[#3E2723] font-medium text-[11px] sm:text-sm">{item.title}</p>
                  <p className="text-[#786E66] text-[9px] sm:text-xs hidden sm:block">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURED STYLES ===== */}
      <section className="py-16 sm:py-24" data-testid="featured-hairstyles-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <div className="text-center mb-10 sm:mb-16">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Crown className="h-5 w-5 text-[#C9A961]" strokeWidth={1.5} />
              <p className="text-[#C9A961] text-xs sm:text-sm font-medium tracking-[0.15em] uppercase">Featured Styles</p>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#3E2723] tracking-tight">Our Most Loved Hairstyles</h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {hairstyles.map((style, idx) => (
              <motion.div
                key={style.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                viewport={{ once: true }}
                className="group"
                data-testid={`hairstyle-card-${idx}`}
              >
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.04)] border border-[#E6DCD3] overflow-hidden">
                  <div className="overflow-hidden bg-[#F3EBE1] relative">
                    {style.cover_image || (style.images && style.images.length > 0) ? (
                      <img
                        src={getPublicImageUrl(style.cover_image || style.images[0])}
                        alt={style.name}
                        className="w-full h-48 sm:h-64 lg:h-72 object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-48 sm:h-64 lg:h-72 flex items-center justify-center">
                        <p className="text-xs text-[#786E66]">No image</p>
                      </div>
                    )}
                  </div>
                  <div className="p-3 sm:p-5">
                    <h3 className="font-serif text-sm sm:text-lg text-[#3E2723] mb-0.5 sm:mb-1 leading-tight">{style.name}</h3>
                    <p className="text-[#786E66] text-[10px] sm:text-xs mb-2 sm:mb-3">{style.category}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-[#3E2723] font-semibold text-sm sm:text-base">
                        From ₦{style.price_ngn?.toLocaleString()}
                      </p>
                      <button
                        onClick={() => {
                          const result = getBookingAction(style.id);
                          if (result.action === 'navigate') {
                            navigate(result.path);
                          } else {
                            toast.info(result.message);
                          }
                        }}
                        className="h-8 w-8 sm:h-9 sm:w-9 rounded-full border border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961] hover:text-white flex items-center justify-center transition-all"
                        data-testid={`home-book-button-${idx}`}
                        title="Book this style"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10 sm:mt-12">
            <Link
              to="/gallery"
              className="bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-full px-8 py-3 inline-flex items-center gap-2 text-sm sm:text-base transition-all hover:-translate-y-0.5"
              data-testid="view-all-button"
            >
              View All Styles
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== ABOUT SECTION ===== */}
      <section className="py-16 sm:py-24 bg-white" data-testid="brand-intro-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-10 sm:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-serif text-3xl sm:text-4xl text-[#3E2723] mb-6 tracking-tight">Welcome to {brandSettings?.business_name || 'Crowns by Preshy'}</h2>
              <p className="text-[#1A1A1A] leading-relaxed mb-4 text-sm sm:text-base">
                We are a premium hair styling service dedicated to making every woman feel like royalty. Our expert stylists specialize in braiding, weaving, cornrows, wigs, and natural hair care.
              </p>
              <p className="text-[#786E66] leading-relaxed mb-6 text-sm sm:text-base">
                With years of experience and a passion for beauty, we create stunning hairstyles that reflect your unique personality and style.
              </p>
              <Link to="/about" className="text-[#3E2723] font-medium inline-flex items-center gap-2 hover:gap-3 transition-all text-sm sm:text-base">
                Learn More About Us <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.pexels.com/photos/853427/pexels-photo-853427.jpeg"
                alt="About Crowns by Preshy"
                className="rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== WHY CHOOSE US ===== */}
      <section className="py-16 sm:py-24" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#3E2723] mb-3 tracking-tight">Why Choose Us</h2>
            <p className="text-[#786E66] text-sm sm:text-base">Excellence in every strand</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {[
              { icon: Crown, title: 'Expert Stylists', desc: 'Years of experience in premium hair styling' },
              { icon: Star, title: 'Quality Service', desc: 'Attention to detail in every appointment' },
              { icon: MessageCircle, title: 'Easy Booking', desc: 'Simple online booking system' },
              { icon: Sparkles, title: '24/7 Support', desc: 'Always here to answer your questions' }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 shadow-[0_4px_20px_rgb(0,0,0,0.04)] border border-[#E6DCD3] text-center"
              >
                <item.icon className="h-8 w-8 sm:h-12 sm:w-12 text-[#C9A961] mx-auto mb-3 sm:mb-4" strokeWidth={1.25} />
                <h3 className="font-serif text-base sm:text-xl text-[#3E2723] mb-1 sm:mb-2">{item.title}</h3>
                <p className="text-[#786E66] text-xs sm:text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      {testimonials.length > 0 && (
        <section className="py-16 sm:py-24 bg-[#F3EBE1]" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
            <div className="text-center mb-10 sm:mb-16">
              <h2 className="font-serif text-3xl sm:text-4xl text-[#3E2723] mb-3 tracking-tight">What Our Clients Say</h2>
              <p className="text-[#786E66] text-sm sm:text-base">Real experiences from beautiful women</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 sm:gap-8">
              {testimonials.map((testimonial, idx) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgb(0,0,0,0.04)]"
                >
                  <div className="flex gap-1 mb-3 sm:mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-3.5 w-3.5 sm:h-4 sm:w-4 fill-[#C9A961] text-[#C9A961]" />
                    ))}
                  </div>
                  <p className="text-[#1A1A1A] mb-3 sm:mb-4 leading-relaxed text-sm sm:text-base">"{testimonial.content}"</p>
                  <p className="text-[#3E2723] font-medium text-sm sm:text-base">{testimonial.customer_name}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== CTA SECTION ===== */}
      <section className="py-16 sm:py-24" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <div className="bg-[#3E2723] rounded-2xl sm:rounded-3xl p-8 sm:p-12 md:p-16 text-center">
            <h2 className="font-serif text-3xl sm:text-4xl text-white mb-4 sm:mb-6 tracking-tight">Ready to Book Your Appointment?</h2>
            <p className="text-white/80 mb-6 sm:mb-8 max-w-2xl mx-auto text-sm sm:text-base">
              Join hundreds of satisfied clients who trust us with their hair. Book your appointment today and experience the Crowns by Preshy difference.
            </p>
            <div className="flex flex-wrap gap-3 sm:gap-4 justify-center">
              <Link
                to="/gallery"
                className="bg-white text-[#3E2723] hover:bg-[#F3EBE1] rounded-full px-6 sm:px-8 py-3 text-sm sm:text-base transition-all hover:-translate-y-0.5"
                data-testid="cta-book-now"
              >
                Book Now
              </Link>
              {brandSettings?.whatsapp_link && (
                <button
                  onClick={whatsappClick}
                  className="border border-white text-white hover:bg-white hover:text-[#3E2723] rounded-full px-6 sm:px-8 py-3 inline-flex items-center gap-2 text-sm sm:text-base transition-all"
                  data-testid="cta-whatsapp"
                >
                  <MessageCircle className="h-4 w-4" />
                  Chat on WhatsApp
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
